import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY } from '../../api/api';
import ItemQuantityField from '../../components/ItemQuantityField';
import { SearchItem } from '../../components/SearchSelect';
import { createIdempotencyKey } from '../../utils/operationKey';
import { openAndPrintHtml } from '../../utils/printDocument';
import './SemiFinishedProductionPage.css';

type Requirement = {
  id: number;
  name: string;
  kind: 'product' | 'semi';
  weightType: string;
  bruttoWeight: number;
  nettoWeight: number;
  output: number;
  requiredBrutto: number;
  requiredNetto: number;
  requiredOutput: number;
};

type SemiFinishedRow = {
  id: number;
  name: string;
  weightType: string;
  netWeight: number;
  grossWeight: number;
  cookingTime: number;
  serveTime: number;
  availableQuantity: number;
  requirements: Requirement[];
};

type Employee = { id: number; name: string };
type Preview = {
  semiFinishedProductId: number;
  semiFinishedProductName: string;
  weightType: string;
  availableQuantity: number;
  baseOutput: number;
  plannedQuantity: number;
  scaleFactor: number;
  estimatedCookingSeconds: number;
  estimatedServeSeconds: number;
  requirements: Requirement[];
};
type ProductionTask = {
  id: number;
  semiFinishedProductId: number;
  semiFinishedProductName: string;
  weightType: string;
  plannedQuantity: number;
  plannedDate: string;
  status: string;
  assignedUserId: string | null;
  assignedUserName: string;
  assignedEmploymentId: number | null;
  assignedEmploymentName: string | null;
  estimatedCookingSeconds: number;
  estimatedServeSeconds: number;
  notes: string;
  createdByUsername: string;
  createdAtUtc: string;
  completedAtUtc: string | null;
  semiFinishedProductionId: number | null;
  recipeBaseOutput: number;
  hasRecipeSnapshot: boolean;
  requirements: Array<{
    id: number;
    name: string;
    kind: 'product' | 'semi';
    weightType: string;
    bruttoWeight: number;
    nettoWeight: number;
    output: number;
    requiredBrutto: number;
    requiredNetto: number;
    requiredOutput: number;
    notes?: string;
    operationNotes?: string;
    preparationOperationName?: string | null;
    processingMethodName?: string | null;
    processingTemperatureC?: number | null;
  }>;
};
type Workspace = {
  semiFinishedProducts: SemiFinishedRow[];
  employees: Employee[];
  tasks: ProductionTask[];
  hasMoreTasks: boolean;
  allGroupsOverview?: boolean;
  canManage?: boolean;
  readOnly?: boolean;
};

const panel: React.CSSProperties = {
  background: 'var(--crm-surface)',
  color: 'var(--crm-text)',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-md)',
  padding: 16,
};
const input: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-sm)',
  padding: '9px 10px',
  background: 'var(--crm-input-bg)',
  color: 'var(--crm-text)',
};
const primaryButton: React.CSSProperties = {
  border: '1px solid var(--crm-success)',
  borderRadius: 'var(--crm-radius-sm)',
  padding: '9px 14px',
  color: 'var(--crm-success)',
  background: 'var(--crm-success-bg)',
  fontWeight: 600,
  cursor: 'pointer',
};

const formatDuration = (seconds: number) => {
  if (seconds <= 0) return 'Не вказано';
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours} год ${rest} хв` : `${rest} хв`;
};
const statusLabel: Record<string, string> = {
  Planned: 'Заплановано',
  InProgress: 'У роботі',
  Completed: 'Виконано',
  Cancelled: 'Скасовано',
};
const localDate = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
const errorMessage = (error: any, fallback: string) => error?.response?.data?.message || fallback;
const formatDate = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('uk-UA');
};

const SemiFinishedProductionPage: React.FC = () => {
  const [workspace, setWorkspace] = useState<Workspace>({ semiFinishedProducts: [], employees: [], tasks: [], hasMoreTasks: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mode, setMode] = useState<'output' | 'ingredient'>('output');
  const [quantity, setQuantity] = useState('');
  const [targetIngredientId, setTargetIngredientId] = useState('');
  const [targetIngredientQuantity, setTargetIngredientQuantity] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [plannedDate, setPlannedDate] = useState(localDate);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadingMoreTasks, setLoadingMoreTasks] = useState(false);
  const [viewingTaskId, setViewingTaskId] = useState<number | null>(null);
  const [contextId, setContextId] = useState(() => localStorage.getItem(OPERATING_GROUP_STORAGE_KEY));
  const [contextChanged, setContextChanged] = useState(false);
  const [writeBlocked, setWriteBlocked] = useState(false);
  const [busyTaskIds, setBusyTaskIds] = useState<number[]>([]);
  const [pendingTaskTransitions, setPendingTaskTransitions] = useState<Record<number, string>>({});
  const workspaceRequest = useRef(0);
  const loadingMoreRef = useRef(false);
  const createInFlight = useRef(false);
  const pendingCreate = useRef<{ key: string; payload: Record<string, unknown> } | null>(null);
  const pendingTransitions = useRef(new Map<number, { key: string; payload: { status: string; actualQuantity?: number } }>());
  const transitionInFlight = useRef(new Set<number>());

  const contextIsCurrent = useCallback((expected: string | null) => (
    !contextChanged && localStorage.getItem(OPERATING_GROUP_STORAGE_KEY) === expected
  ), [contextChanged]);

  const loadWorkspace = useCallback(async (date = plannedDate) => {
    if (contextChanged) return;
    const request = ++workspaceRequest.current;
    const requestedContext = contextId;
    const isCurrent = () => request === workspaceRequest.current && contextIsCurrent(requestedContext);
    setLoading(true);
    setError('');
    try {
      const response = await api.get<Workspace>('/semifinishedproductiontasks/workspace', {
        params: { plannedDate: date, skip: 0, take: 200 },
      });
      if (!isCurrent()) return;
      setWorkspace(response.data);
      setSelectedId((previous) => previous && response.data.semiFinishedProducts.some((item) => item.id === previous)
        ? previous
        : response.data.semiFinishedProducts[0]?.id ?? null);
      if (response.data.readOnly || response.data.canManage === false || response.data.allGroupsOverview) {
        setWriteBlocked(true);
        setError(response.data.allGroupsOverview || contextId === 'all'
          ? 'Огляд усіх ресторанів доступний лише для читання. Оберіть конкретний робочий ресторан для змін.'
          : 'Цей огляд доступний лише для читання. Змініть робочий ресторан або зверніться до адміністратора.');
      } else {
        setWriteBlocked(false);
      }
    } catch (e: any) {
      if (!isCurrent()) return;
      setWorkspace({ semiFinishedProducts: [], employees: [], tasks: [], hasMoreTasks: false });
      setWriteBlocked(e?.response?.status === 403 || e?.response?.status === 404 || e?.response?.status === 409);
      setError(errorMessage(e, e?.response?.status === 403 || e?.response?.status === 404
        ? 'Немає доступу до виробничих завдань цього ресторану.'
        : 'Не вдалося завантажити виробничий модуль.'));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [contextChanged, contextId, contextIsCurrent, plannedDate]);

  const loadMoreTasks = async () => {
    if (loadingMoreRef.current || readOnly) return;
    loadingMoreRef.current = true;
    setLoadingMoreTasks(true);
    setError('');
    const request = workspaceRequest.current;
    const requestedContext = contextId;
    try {
      const response = await api.get<Workspace>('/semifinishedproductiontasks/workspace', {
        params: { plannedDate, skip: workspace.tasks.length, take: 200 },
      });
      if (request !== workspaceRequest.current || !contextIsCurrent(requestedContext)) return;
      setWorkspace((previous) => ({
        ...previous,
        tasks: [...previous.tasks, ...response.data.tasks],
        hasMoreTasks: response.data.hasMoreTasks,
      }));
    } catch (e: any) {
      if (request === workspaceRequest.current && contextIsCurrent(requestedContext))
        setError(errorMessage(e, 'Не вдалося завантажити попередні виробничі завдання.'));
    } finally {
      loadingMoreRef.current = false;
      if (request === workspaceRequest.current && contextIsCurrent(requestedContext)) setLoadingMoreTasks(false);
    }
  };

  useEffect(() => {
    void loadWorkspace(plannedDate);
  }, [loadWorkspace, plannedDate]);

  useEffect(() => {
    const handleContextChange = (event: StorageEvent) => {
      if (event.key !== OPERATING_GROUP_STORAGE_KEY || event.newValue === contextId) return;
      workspaceRequest.current += 1;
      setContextChanged(true);
      setContextId(event.newValue);
      setWorkspace({ semiFinishedProducts: [], employees: [], tasks: [], hasMoreTasks: false });
      setSelectedId(null);
      setPreview(null);
      setViewingTaskId(null);
      setBusyTaskIds([]);
      setPendingTaskTransitions({});
      loadingMoreRef.current = false;
      setLoadingMoreTasks(false);
      pendingCreate.current = null;
      pendingTransitions.current.clear();
      setLoading(false);
      setError('Робочий ресторан змінено в іншій вкладці. Дані попереднього ресторану очищено; оновіть сторінку для продовження.');
    };
    window.addEventListener('storage', handleContextChange);
    return () => window.removeEventListener('storage', handleContextChange);
  }, [contextId]);

  useEffect(() => {
    const handleOperatingContextChange = (event: Event) => {
      const nextContext = (event as CustomEvent<{ groupId: string | null }>).detail?.groupId;
      window.setTimeout(() => {
        const storedContext = localStorage.getItem(OPERATING_GROUP_STORAGE_KEY);
        if (storedContext !== nextContext || contextId === nextContext) return;
        workspaceRequest.current += 1;
        loadingMoreRef.current = false;
        setLoadingMoreTasks(false);
        setWorkspace({ semiFinishedProducts: [], employees: [], tasks: [], hasMoreTasks: false });
        setSelectedId(null);
        setPreview(null);
        setViewingTaskId(null);
        setBusyTaskIds([]);
        setPendingTaskTransitions({});
        pendingCreate.current = null;
        pendingTransitions.current.clear();
        setContextChanged(false);
        setContextId(nextContext);
        setError('');
        setLoading(true);
      }, 0);
    };
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, handleOperatingContextChange);
    return () => window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, handleOperatingContextChange);
  }, [contextId]);

  const selected = workspace.semiFinishedProducts.find((item) => item.id === selectedId) ?? null;
  const viewingTask = workspace.tasks.find((task) => task.id === viewingTaskId) ?? null;
  const taskScale = viewingTask && viewingTask.recipeBaseOutput > 0
    ? viewingTask.plannedQuantity / viewingTask.recipeBaseOutput
    : 0;
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('uk');
    return workspace.semiFinishedProducts.filter((item) => !term || item.name.toLocaleLowerCase('uk').includes(term));
  }, [workspace.semiFinishedProducts, search]);
  const ingredientItems = useMemo<SearchItem[]>(() => selected?.requirements.map((item) => ({
    id: String(item.id),
    name: `${item.name} · ${item.kind === 'semi' ? 'Н/Ф' : 'продукт'}`,
  })) ?? [], [selected]);
  const selectedIngredient = selected?.requirements.find((item) => String(item.id) === targetIngredientId) ?? null;
  const readOnly = contextChanged || contextId === 'all' || writeBlocked || workspace.readOnly === true
    || workspace.canManage === false || workspace.allGroupsOverview === true;

  useEffect(() => {
    setPreview(null);
    if (!selectedId) return;
    const params: Record<string, number> = { semiFinishedProductId: selectedId };
    if (mode === 'output') {
      const value = Number(quantity);
      if (!Number.isFinite(value) || value <= 0) return;
      params.quantity = value;
    } else {
      const itemId = Number(targetIngredientId);
      const value = Number(targetIngredientQuantity);
      if (!itemId || !Number.isFinite(value) || value <= 0) return;
      params.targetIngredientId = itemId;
      params.targetIngredientQuantity = value;
    }

    let active = true;
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      api.get<Preview>('/semifinishedproductiontasks/preview', { params })
        .then((response) => { if (active) setPreview(response.data); })
        .catch((e: any) => { if (active) setError(e?.response?.data?.message || 'Не вдалося розрахувати рецептуру.'); })
        .finally(() => { if (active) setPreviewLoading(false); });
    }, 180);
    return () => { active = false; window.clearTimeout(timer); };
  }, [selectedId, mode, quantity, targetIngredientId, targetIngredientQuantity, contextId, contextChanged]);

  const selectSemiFinished = (id: number) => {
    setSelectedId(id);
    setQuantity('');
    setTargetIngredientId('');
    setTargetIngredientQuantity('');
    setPreview(null);
    setMessage('');
    setError('');
  };

  const createTask = async () => {
    if (!selected || !preview || readOnly || createInFlight.current) return;
    const payload = pendingCreate.current?.payload ?? {
      semiFinishedProductId: selected.id,
      plannedQuantity: preview.plannedQuantity,
      plannedDate,
      assignedEmploymentId: assigneeId ? Number(assigneeId) : null,
      assignedUserId: null,
      notes,
      targetIngredientId: mode === 'ingredient' ? Number(targetIngredientId) : null,
      targetIngredientQuantity: mode === 'ingredient' ? Number(targetIngredientQuantity) : null,
    };
    if (!pendingCreate.current) pendingCreate.current = { key: createIdempotencyKey(), payload };
    const operation = pendingCreate.current;
    createInFlight.current = true;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.post<ProductionTask>('/semifinishedproductiontasks', operation.payload, {
        headers: { 'Idempotency-Key': operation.key },
      });
      if (!contextIsCurrent(contextId)) return;
      pendingCreate.current = null;
      setMessage('Виробниче завдання створено. Залишок зміниться лише після позначення завдання як виконаного.');
      setNotes('');
      await loadWorkspace();
    } catch (e: any) {
      if (!contextIsCurrent(contextId)) return;
      if (e?.response?.status === 403 || e?.response?.status === 404) setWriteBlocked(true);
      if (e?.response?.status === 409) {
        setMessage('Отримано конфлікт або підтвердження попередньої спроби. Оновлюємо завдання; повторна спроба збереже той самий ключ.');
        await loadWorkspace();
      }
      setError(errorMessage(e, e?.response?.status === 403 || e?.response?.status === 404
        ? 'Доступ до запису виробничих завдань відкликано. Дані не було повторно надіслано.'
        : 'Не вдалося створити виробниче завдання. Повторіть спробу без зміни даних.'));
    } finally {
      createInFlight.current = false;
      setSaving(false);
    }
  };

  const setTaskStatus = async (task: ProductionTask, status: 'InProgress' | 'Completed' | 'Cancelled') => {
    if (readOnly || transitionInFlight.current.has(task.id)) return;
    let actualQuantity: number | undefined;
    const existingOperation = pendingTransitions.current.get(task.id);
    if (existingOperation) {
      status = existingOperation.payload.status as typeof status;
      actualQuantity = existingOperation.payload.actualQuantity;
    } else if (status === 'Completed') {
      const entered = window.prompt(`Фактично виготовлена кількість (${task.weightType}):`, String(task.plannedQuantity));
      if (entered === null) return;
      actualQuantity = Number(entered.replace(',', '.'));
      if (!Number.isFinite(actualQuantity) || actualQuantity <= 0) {
        setError('Вкажіть фактичну кількість більше нуля.');
        return;
      }
    }
    if (status === 'Cancelled' && !window.confirm(`Скасувати завдання «${task.semiFinishedProductName}»?`)) return;
    const payload = existingOperation?.payload ?? { status, actualQuantity };
    if (!existingOperation) {
      pendingTransitions.current.set(task.id, { key: createIdempotencyKey(), payload });
      setPendingTaskTransitions((current) => ({ ...current, [task.id]: status }));
    }
    const operation = pendingTransitions.current.get(task.id)!;
    transitionInFlight.current.add(task.id);
    setBusyTaskIds((ids) => ids.includes(task.id) ? ids : [...ids, task.id]);
    setError('');
    setMessage('');
    try {
      await api.put(`/semifinishedproductiontasks/${task.id}/status`, operation.payload, {
        headers: { 'Idempotency-Key': operation.key },
      });
      if (!contextIsCurrent(contextId)) return;
      pendingTransitions.current.delete(task.id);
      setPendingTaskTransitions((current) => {
        const { [task.id]: _pending, ...rest } = current;
        return rest;
      });
      setMessage(status === 'Completed' ? 'Виробництво зафіксовано, залишок оновлено.' : 'Статус завдання оновлено.');
      await loadWorkspace();
    } catch (e: any) {
      if (!contextIsCurrent(contextId)) return;
      if (e?.response?.status === 403 || e?.response?.status === 404) setWriteBlocked(true);
      if (e?.response?.status === 409) {
        setMessage('Статус уже змінено або спробу підтверджено. Оновлюємо завдання; повторна спроба збереже той самий ключ.');
        await loadWorkspace();
      }
      setError(errorMessage(e, e?.response?.status === 403 || e?.response?.status === 404
        ? 'Доступ до запису виробничих завдань відкликано. Зміну не було повторно надіслано.'
        : 'Не вдалося оновити завдання. Повторіть ту саму дію для безпечної перевірки.'));
    } finally {
      transitionInFlight.current.delete(task.id);
      setBusyTaskIds((ids) => ids.filter((id) => id !== task.id));
    }
  };

  const printTask = (task: ProductionTask) => {
    const baseOutput = task.recipeBaseOutput;
    if (baseOutput <= 0) {
      setError(task.hasRecipeSnapshot
        ? 'Неможливо надрукувати рецептуру: у збереженій версії не задано вихід.'
        : 'Для цього старого завдання версію рецептури не збережено.');
      return;
    }
    const scale = task.plannedQuantity / baseOutput;
    const escapeHtml = (value: string) => value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const rows = task.requirements.map((item) => `
      <tr>
        <td>${escapeHtml(item.name)}</td>
        <td>${item.kind === 'semi' ? 'Н/Ф' : 'Продукт'}</td>
        <td class="number">${item.requiredBrutto.toFixed(3)} ${escapeHtml(item.weightType)}</td>
        <td class="number">${item.requiredNetto.toFixed(3)} ${escapeHtml(item.weightType)}</td>
        <td class="number">${item.requiredOutput.toFixed(3)} ${escapeHtml(item.weightType)}</td>
        <td>${escapeHtml([
          item.preparationOperationName,
          item.processingMethodName,
          item.processingTemperatureC != null ? `${item.processingTemperatureC} °C` : '',
          item.operationNotes,
          item.notes,
        ].filter(Boolean).join(' · '))}</td>
      </tr>
    `).join('');
    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Виробниче завдання #${task.id}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937}
        h1,h2{margin:0 0 12px}
        h2{margin-top:22px;font-size:17px}
        .meta{font-size:13px;margin:5px 0}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th,td{border:1px solid #cbd5e1;padding:7px 8px;font-size:12px;text-align:left}
        th{background:#f1f5f9}
        .number{text-align:right;white-space:nowrap}
        @media print{body{padding:0}thead{display:table-header-group}tr{break-inside:avoid}}
      </style></head><body>
      <h1>Виробниче завдання #${task.id}</h1>
      <div class="meta">Напівфабрикат: <strong>${escapeHtml(task.semiFinishedProductName)}</strong></div>
      <div class="meta">Планова кількість: <strong>${task.plannedQuantity.toFixed(3)} ${escapeHtml(task.weightType)}</strong></div>
      <div class="meta">Дата плану: <strong>${formatDate(task.plannedDate)}</strong></div>
      <div class="meta">Статус: <strong>${escapeHtml(statusLabel[task.status] ?? task.status)}</strong></div>
      <div class="meta">Відповідальний: <strong>${escapeHtml(task.assignedEmploymentName ?? task.assignedUserName ?? 'Не призначено')}</strong></div>
      <div class="meta">Орієнтовний час приготування: <strong>${escapeHtml(formatDuration(task.estimatedCookingSeconds))}</strong></div>
      <div class="meta">Орієнтовний час видачі: <strong>${escapeHtml(formatDuration(task.estimatedServeSeconds))}</strong></div>
      ${task.notes ? `<div class="meta">Примітка: <strong>${escapeHtml(task.notes)}</strong></div>` : ''}
      ${task.semiFinishedProductionId ? `<div class="meta">Документ виробництва: <strong>#${task.semiFinishedProductionId}</strong></div>` : ''}
      <h2>Збережена рецептура на планову кількість · множник ×${scale.toFixed(3)}</h2>
      <table><thead><tr><th>Інгредієнт</th><th>Тип</th><th class="number">Брутто</th><th class="number">Нетто</th><th class="number">Вихід</th><th>Операція / примітки</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6">Рецептура не заповнена.</td></tr>'}</tbody></table>
      </body></html>`;

    void openAndPrintHtml(html, {
      minimumDelayMs: 250,
      onPopupBlocked: () => setError('Браузер заблокував вікно друку. Дозвольте спливаючі вікна та спробуйте ще раз.'),
      onError: () => setError('Не вдалося підготувати документ для друку.'),
    });
  };

  if (loading) return <div style={{ padding: 24 }}>Завантаження виробничого модуля...</div>;

  return (
    <div style={{ maxWidth: 1500, margin: '0 auto', padding: 20, color: 'var(--crm-text)' }}>
      <h2 style={{ marginTop: 0 }}>Планування виробництва Н/Ф</h2>
      <p style={{ color: 'var(--crm-text-muted)', marginTop: -8 }}>
        Оберіть напівфабрикат, масштабуйте рецепт і призначте відповідального. Запланована кількість не змінює складський залишок до фактичного завершення.
      </p>
      {error && <div role="alert" style={{ ...panel, color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', borderColor: 'var(--crm-danger)', marginBottom: 12 }}>{error}</div>}
      {readOnly && !error && <div role="alert" style={{ ...panel, color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', borderColor: 'var(--crm-danger)', marginBottom: 12 }}>
        {contextId === 'all' || workspace.allGroupsOverview
          ? 'Огляд усіх ресторанів доступний лише для читання. Оберіть конкретний робочий ресторан для змін.'
          : 'Виробничі дані доступні лише для читання в поточному контексті.'}
      </div>}
      {message && <div role="status" style={{ ...panel, color: 'var(--crm-success)', background: 'var(--crm-success-bg)', borderColor: 'var(--crm-success)', marginBottom: 12 }}>{message}</div>}

      <div className="semi-finished-production-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 0.8fr) minmax(0, 2fr)', gap: 16, alignItems: 'start' }}>
        <section style={panel}>
          <h3 style={{ marginTop: 0 }}>Н/Ф в наявності</h3>
          <input style={input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Пошук напівфабрикату..." />
          <div style={{ display: 'grid', gap: 7, marginTop: 10, maxHeight: 650, overflowY: 'auto' }}>
            {filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => selectSemiFinished(item.id)}
                style={{
                  textAlign: 'left', borderRadius: 7, padding: '10px 11px',
                  border: `1px solid ${selectedId === item.id ? 'var(--crm-info)' : 'var(--crm-border)'}`,
                  background: selectedId === item.id ? 'var(--crm-info-bg)' : 'var(--crm-surface)',
                  color: 'var(--crm-text)', cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 650 }}>{item.name}</div>
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 3 }}>
                  Наявно: {Number(item.availableQuantity || 0).toFixed(3)} {item.weightType}
                  {item.requirements.length === 0 && <span style={{ color: 'var(--crm-danger)' }}> · немає рецептури</span>}
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div style={{ padding: 12, color: 'var(--crm-text-muted)' }}>Нічого не знайдено.</div>}
          </div>
        </section>

        <div style={{ display: 'grid', gap: 16 }}>
          {!selected ? (
            <section style={panel}>Створіть Н/Ф або змініть пошук, щоб почати розрахунок.</section>
          ) : (
            <section style={panel}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'start' }}>
                <div>
                  <h3 style={{ margin: '0 0 5px' }}>{selected.name}</h3>
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>
                    Наявний залишок: <strong>{Number(selected.availableQuantity || 0).toFixed(3)} {selected.weightType}</strong>
                    {' · '}Вихід рецепта: {Number(selected.netWeight || selected.grossWeight || 0).toFixed(3)} {selected.weightType}
                  </div>
                </div>
                <a href="/management/semi-finished" style={{ color: 'var(--crm-info)', fontSize: 13 }}>Редагувати рецептуру →</a>
              </div>

              <div style={{ display: 'flex', gap: 8, margin: '16px 0 12px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setMode('output')} style={{ ...primaryButton, background: mode === 'output' ? 'var(--crm-info-bg)' : 'var(--crm-surface-alt)', color: mode === 'output' ? 'var(--crm-info)' : 'var(--crm-text-muted)', borderColor: mode === 'output' ? 'var(--crm-info)' : 'var(--crm-border)' }}>
                  Потрібна кількість Н/Ф
                </button>
                <button type="button" onClick={() => setMode('ingredient')} style={{ ...primaryButton, background: mode === 'ingredient' ? 'var(--crm-info-bg)' : 'var(--crm-surface-alt)', color: mode === 'ingredient' ? 'var(--crm-info)' : 'var(--crm-text-muted)', borderColor: mode === 'ingredient' ? 'var(--crm-info)' : 'var(--crm-border)' }}>
                  Розрахувати від інгредієнта
                </button>
              </div>

              {mode === 'output' ? (
                <label style={{ display: 'block', maxWidth: 320, fontSize: 13, fontWeight: 600 }}>
                  Скільки Н/Ф потрібно ({selected.weightType})?
                  <input autoFocus type="number" min="0" step="0.001" style={{ ...input, marginTop: 5 }} value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Введіть необхідну кількість" />
                </label>
              ) : (
                <div className="semi-finished-production-ingredient-form" style={{ maxWidth: 700 }}>
                    <ItemQuantityField
                      items={ingredientItems}
                      value={targetIngredientId}
                      onChange={setTargetIngredientId}
                      quantity={targetIngredientQuantity}
                      onQuantityChange={setTargetIngredientQuantity}
                      label="Інгредієнт рецептури"
                      ariaLabel="Інгредієнт рецептури"
                      quantityLabel="Потрібна кількість"
                      unit={selectedIngredient?.weightType}
                      min={0.001}
                      step={0.001}
                      quantityRequired
                    />
                </div>
              )}

              {previewLoading && <div style={{ marginTop: 12, color: 'var(--crm-text-muted)' }}>Перерахунок рецептури...</div>}
              {preview && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 14 }}>
                    <div style={{ ...panel, padding: 10 }}><small>Плановий вихід</small><div style={{ fontSize: 19, fontWeight: 700 }}>{preview.plannedQuantity.toFixed(3)} {preview.weightType}</div></div>
                    <div style={{ ...panel, padding: 10 }}><small>Множник рецепта</small><div style={{ fontSize: 19, fontWeight: 700 }}>×{preview.scaleFactor.toFixed(3)}</div></div>
                    <div style={{ ...panel, padding: 10 }}><small>Час приготування, оцінка</small><div style={{ fontSize: 19, fontWeight: 700 }}>{formatDuration(preview.estimatedCookingSeconds)}</div></div>
                    <div style={{ ...panel, padding: 10 }}><small>Час видачі, оцінка</small><div style={{ fontSize: 19, fontWeight: 700 }}>{formatDuration(preview.estimatedServeSeconds)}</div></div>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                    Час масштабується пропорційно обсягу за значеннями рецептури; це планова оцінка, а не гарантований строк. Залишок на складі тут показаний для довідки та не віднімається автоматично.
                  </p>
                  <div style={{ overflowX: 'auto', marginTop: 10 }}>
                    <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--crm-surface-alt)' }}>
                          {['Інгредієнт', 'Тип', 'Брутто', 'Нетто', 'Вихід', 'Операція / примітки'].map((header) => <th key={header} style={{ textAlign: header === 'Інгредієнт' || header === 'Тип' || header === 'Операція / примітки' ? 'left' : 'right', padding: 9, borderBottom: '1px solid var(--crm-border)' }}>{header}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.requirements.map((item) => (
                          <tr key={item.id}>
                            <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.name}</td>
                            <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.kind === 'semi' ? 'Н/Ф' : 'Продукт'}</td>
                            <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)', textAlign: 'right' }}>{item.requiredBrutto.toFixed(3)} {item.weightType}</td>
                            <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)', textAlign: 'right' }}>{item.requiredNetto.toFixed(3)} {item.weightType}</td>
                            <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)', textAlign: 'right' }}>{item.requiredOutput.toFixed(3)} {item.weightType}</td>
                          </tr>
                        ))}
                        {preview.requirements.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: 'var(--crm-danger)' }}>Для цього Н/Ф не заповнено рецептуру.</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  <div className="semi-finished-production-task-form" style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(190px, 1fr) minmax(220px, 2fr)', gap: 10, marginTop: 14 }}>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>Дата плану
                      <input type="date" style={{ ...input, marginTop: 5 }} value={plannedDate} onChange={(e) => {
                        workspaceRequest.current += 1;
                        loadingMoreRef.current = false;
                        setLoadingMoreTasks(false);
                        setLoading(true);
                        setPlannedDate(e.target.value);
                        setWorkspace((current) => ({ ...current, tasks: [], hasMoreTasks: false }));
                        setViewingTaskId(null);
                      }} />
                    </label>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>Відповідальний
                      <select style={{ ...input, marginTop: 5 }} value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} disabled={readOnly}>
                        <option value="">Не призначено</option>
                        {workspace.employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
                      </select>
                    </label>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>Примітка
                      <input style={{ ...input, marginTop: 5 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Наприклад, для обіду / заходу..." />
                    </label>
                  </div>
                  <button type="button" onClick={createTask} disabled={saving || readOnly || !preview || preview.requirements.length === 0} style={{ ...primaryButton, marginTop: 12, opacity: saving || readOnly || preview.requirements.length === 0 ? 0.6 : 1 }}>
                    {saving ? 'Створення...' : pendingCreate.current ? 'Повторити створення з початковими даними' : 'Створити виробниче завдання'}
                  </button>
                </>
              )}
            </section>
          )}

          <section style={panel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0 }}>Виробничі завдання</h3>
              <span style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>Заплановане виробництво не входить у залишок до фактичного завершення.</span>
            </div>
            {workspace.tasks.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Активних або нещодавніх виробничих завдань немає.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                {workspace.tasks.map((task) => (
                  <div key={task.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 11, background: 'var(--crm-surface)' }}>
                    <div>
                      <strong>{task.semiFinishedProductName}</strong> · {task.plannedQuantity.toFixed(3)} {task.weightType}
                      <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginTop: 4 }}>
                        {formatDate(task.plannedDate)} · {statusLabel[task.status] ?? task.status}
                        {' · '}Відповідальний: {task.assignedEmploymentName ?? task.assignedUserName ?? 'не призначено'}
                        {' · '}Приготування: {formatDuration(task.estimatedCookingSeconds)}
                        {task.notes ? ` · ${task.notes}` : ''}
                      </div>
                    </div>
                    <div className="production-task-actions" style={{ display: 'flex', gap: 6 }}>
                      <button type="button" onClick={() => setViewingTaskId(task.id)} style={{ ...primaryButton, background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', borderColor: 'var(--crm-border)' }}>Переглянути</button>
                      {task.status === 'Planned' && (!pendingTaskTransitions[task.id] || pendingTaskTransitions[task.id] === 'InProgress') && <button type="button" disabled={readOnly || busyTaskIds.includes(task.id)} onClick={() => setTaskStatus(task, 'InProgress')} style={{ ...primaryButton, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)' }}>{pendingTaskTransitions[task.id] ? 'Повторити початок' : 'Почати'}</button>}
                      {(task.status === 'Planned' || task.status === 'InProgress') && (!pendingTaskTransitions[task.id] || pendingTaskTransitions[task.id] === 'Completed') && <button type="button" disabled={readOnly || busyTaskIds.includes(task.id)} onClick={() => setTaskStatus(task, 'Completed')} style={primaryButton}>{pendingTaskTransitions[task.id] ? 'Повторити завершення' : 'Завершити'}</button>}
                      {(task.status === 'Planned' || task.status === 'InProgress') && (!pendingTaskTransitions[task.id] || pendingTaskTransitions[task.id] === 'Cancelled') && <button type="button" disabled={readOnly || busyTaskIds.includes(task.id)} onClick={() => setTaskStatus(task, 'Cancelled')} style={{ ...primaryButton, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderColor: 'var(--crm-danger)' }}>{pendingTaskTransitions[task.id] ? 'Повторити скасування' : 'Скасувати'}</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {workspace.hasMoreTasks && (
              <button
                type="button"
                onClick={loadMoreTasks}
                disabled={loadingMoreTasks || readOnly}
                style={{ ...primaryButton, marginTop: 12, background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', borderColor: 'var(--crm-border)', opacity: loadingMoreTasks ? 0.7 : 1 }}
              >
                {loadingMoreTasks ? 'Завантаження...' : 'Завантажити попередні завдання'}
              </button>
            )}
          </section>
        </div>
      </div>
      {viewingTask && (
        <div
          role="presentation"
          onMouseDown={(event) => { if (event.target === event.currentTarget) setViewingTaskId(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15, 23, 42, .55)', display: 'grid', placeItems: 'center', padding: 16 }}
        >
          <section role="dialog" aria-modal="true" aria-labelledby="production-task-title" style={{ ...panel, width: 'min(900px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
              <div>
                <h2 id="production-task-title" style={{ margin: '0 0 5px' }}>Виробниче завдання #{viewingTask.id}</h2>
                <strong>{viewingTask.semiFinishedProductName}</strong> · {viewingTask.plannedQuantity.toFixed(3)} {viewingTask.weightType}
              </div>
              <button type="button" aria-label="Закрити перегляд завдання" onClick={() => setViewingTaskId(null)} style={{ ...primaryButton, background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', borderColor: 'var(--crm-border)' }}>Закрити</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 14 }}>
              <div style={{ ...panel, padding: 10 }}><small>Дата плану</small><div>{formatDate(viewingTask.plannedDate)}</div></div>
              <div style={{ ...panel, padding: 10 }}><small>Статус</small><div>{statusLabel[viewingTask.status] ?? viewingTask.status}</div></div>
              <div style={{ ...panel, padding: 10 }}><small>Відповідальний</small><div>{viewingTask.assignedEmploymentName ?? viewingTask.assignedUserName ?? 'Не призначено'}</div></div>
              <div style={{ ...panel, padding: 10 }}><small>Час приготування</small><div>{formatDuration(viewingTask.estimatedCookingSeconds)}</div></div>
              <div style={{ ...panel, padding: 10 }}><small>Час видачі</small><div>{formatDuration(viewingTask.estimatedServeSeconds)}</div></div>
              {viewingTask.semiFinishedProductionId && <div style={{ ...panel, padding: 10 }}><small>Документ виробництва</small><div>#{viewingTask.semiFinishedProductionId}</div></div>}
            </div>
            {viewingTask.notes && <p><strong>Примітка:</strong> {viewingTask.notes}</p>}
            <h3 style={{ marginBottom: 8 }}>Рецептура на планову кількість · множник ×{taskScale.toFixed(3)}</h3>
            {viewingTask.hasRecipeSnapshot && taskScale > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ background: 'var(--crm-surface-alt)' }}>
                    {['Інгредієнт', 'Тип', 'Брутто', 'Нетто', 'Вихід'].map((header) => <th key={header} style={{ textAlign: header === 'Інгредієнт' || header === 'Тип' ? 'left' : 'right', padding: 9, borderBottom: '1px solid var(--crm-border)' }}>{header}</th>)}
                  </tr></thead>
                  <tbody>
                    {viewingTask.requirements.map((item) => (
                      <tr key={item.id}>
                        <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.name}</td>
                        <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.kind === 'semi' ? 'Н/Ф' : 'Продукт'}</td>
                        <td style={{ padding: 9, textAlign: 'right', borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.requiredBrutto.toFixed(3)} {item.weightType}</td>
                        <td style={{ padding: 9, textAlign: 'right', borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.requiredNetto.toFixed(3)} {item.weightType}</td>
                        <td style={{ padding: 9, textAlign: 'right', borderBottom: '1px solid var(--crm-table-row-border)' }}>{item.requiredOutput.toFixed(3)} {item.weightType}</td>
                        <td style={{ padding: 9, borderBottom: '1px solid var(--crm-table-row-border)' }}>
                          {[
                            item.preparationOperationName,
                            item.processingMethodName,
                            item.processingTemperatureC != null ? `${item.processingTemperatureC} °C` : '',
                            item.operationNotes,
                            item.notes,
                          ].filter(Boolean).join(' · ') || '—'}
                        </td>
                      </tr>
                    ))}
                    {viewingTask.requirements.length === 0 && <tr><td colSpan={6} style={{ padding: 12 }}>У збереженій версії рецептура не заповнена.</td></tr>}
                  </tbody>
                </table>
              </div>
            ) : (
              <div role="alert" style={{ color: 'var(--crm-danger)' }}>
                {viewingTask.hasRecipeSnapshot
                  ? 'Не вдалося обчислити рецептуру: у збереженій версії не задано вихід.'
                  : 'Для завдання, створеного до збереження версій, рецептура не зафіксована. Поточну рецептуру не показано, щоб не вводити в оману.'}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" onClick={() => printTask(viewingTask)} style={primaryButton}>Друкувати завдання</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default SemiFinishedProductionPage;
